import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { breadcrumbSchema } from "../data/seo";

const smileMakeoverIncludes = [
  "Professional teeth whitening",
  "Cosmetic dental bonding",
  "Porcelain veneers",
  "Dental crowns",
  "Orthodontic treatment",
  "Gum recontouring",
  "Replacement of missing teeth",
];

export const SmileMakeoverPage = () => {
  return (
    <div data-testid="smile-makeover-page">
      <Seo
        title="Smile Makeover | Smiles by Dr. K"
        description="A personalized smile makeover designed to enhance balance, confidence, and natural beauty through cosmetic and restorative dentistry."
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Smile Makeover", path: "/smilemakeover" },
          ]),
        ]}
      />

      <section className="relative h-[65vh] min-h-[500px] overflow-hidden">
        <img src={IMAGES.smileOutdoor} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="relative h-full container-wide flex flex-col justify-end pb-16 text-white">
          <div className="label-eyebrow text-gold mb-6">Smile Makeover</div>
          <h1 className="heading-display text-5xl md:text-7xl lg:text-8xl text-white max-w-5xl">
            A Beautiful Smile, Distinctly Yours
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-24 container-x">
        <div className="max-w-4xl text-ink/80 leading-[1.85] space-y-6">
          <p>
            A smile makeover is not about creating perfection. It is about bringing balance, beauty, and confidence to the smile that already belongs to you.
          </p>
          <p>
            Through a personalized combination of cosmetic and restorative treatments, a smile makeover can refine concerns such as discoloration, chips, wear, uneven shapes, unwanted spaces, misalignment, or missing teeth.
          </p>
          <p>
            The result should never feel artificial or overdone. It should simply feel like you, at your most confident.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/50">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Approach" title="Designed" italicWord="Around You" />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <p>
              Every smile has its own character. The shape of your teeth, the curve of your lips, your facial features, and the way you express yourself all influence what will look natural.
            </p>
            <p>
              A thoughtfully designed smile makeover respects those details. Rather than treating individual teeth in isolation, it considers the smile as a whole: its color, proportions, symmetry, and relationship to your face.
            </p>
            <p>
              Sometimes one subtle change is enough. Other smiles benefit from a carefully planned combination of treatments.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <SectionHeading eyebrow="Possibilities" title="Your Smile Makeover" italicWord="May Include" />
        <div className="mt-10 max-w-3xl">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-ink/80">
            {smileMakeoverIncludes.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-ink/80 leading-[1.85]">
            Only the treatments that genuinely serve your smile should be considered. Oral health comes first, and every recommendation should balance beauty with comfort, function, and the preservation of healthy tooth structure.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ink text-bone">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Craft" title="Beauty in" italicWord="the Details" light />
          </div>
          <div className="lg:col-span-7 leading-[1.8] space-y-6 text-white/85">
            <p>The most elegant cosmetic dentistry does not call attention to itself.</p>
            <p>
              Natural-looking results come from thoughtful details: the softness of a tooth shape, subtle variations in shade, balanced proportions, and a finish that complements rather than competes with your features.
            </p>
            <p>
              The goal is not to give every patient the same smile. It is to create one that could belong only to you.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <div className="max-w-4xl mx-auto border border-gold/25 bg-cream/50 p-8 md:p-12">
          <SectionHeading eyebrow="From Dr. K" title="A Personal" italicWord="Note" align="center" />
          <blockquote className="mt-8 text-center space-y-4">
            <p className="font-serif italic text-xl md:text-2xl leading-[1.7] text-ink/85">
              "I do not believe every smile should look the same. My role is to understand what you would like to change, guide you honestly, and preserve the qualities that make your smile uniquely yours. The best result is one that feels natural, confident, and true to you."
            </p>
            <footer className="text-sm tracking-[0.2em] uppercase text-gold">Dr. K</footer>
          </blockquote>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/40">
        <div className="container-x max-w-4xl text-ink/80 leading-[1.85] space-y-6">
          <SectionHeading eyebrow="Confidence" title="Imagine Smiling" italicWord="Without a Second Thought" />
          <p>
            You do not need to know which treatments you want. Begin with how you would like to feel when you smile.
          </p>
          <p>
            Because the finest smile makeover does not change who you are.
          </p>
          <p>It lets more of you shine through.</p>
          <div className="pt-4">
            <Link to="/contact" className="btn-outline inline-flex">
              Explore Your Smile Makeover
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
